 /**
  * SEO HEADING STRUCTURE - 스터디 스트림 (Korean)
  */
 
 import { Header } from "@/components/Header";
 import { Footer } from "@/components/Footer";
 import { SEO } from "@/components/SEO";
 import { Button } from "@/components/ui/button";
 import { Card, CardContent } from "@/components/ui/card";
 import { Clock, Users, Music, Brain, ArrowRight, Play, Headphones, BookOpen, Target, Trophy, Zap } from "lucide-react";
 import { Link } from "react-router-dom";
 
 const features = [
   {
     icon: Users,
     title: "실시간 동료 학습",
     description: "전 세계 학생들과 함께 실시간으로 공부하세요. 혼자가 아닌 함께하는 집중.",
   },
   {
     icon: Clock,
     title: "집중 타이머",
     description: "포모도로 타이머로 집중 세션을 관리하고, 휴식 시간을 효과적으로 활용하세요.",
   },
   {
     icon: Music,
     title: "로파이 음악",
     description: "집중력을 높이는 로파이 음악과 함께 더 깊은 몰입 상태를 경험하세요.",
   },
   {
     icon: Brain,
     title: "과학적 학습법",
     description: "사회적 촉진 효과와 바디 더블링 기법을 활용한 과학적인 학습 방법.",
   },
   {
     icon: Target,
     title: "목표 설정",
     description: "세션별 목표를 설정하고 진행 상황을 추적하여 성취감을 높이세요.",
   },
   {
     icon: Trophy,
     title: "리더보드",
     description: "다른 학습자들과 경쟁하며 동기부여를 받고 학습 효율을 높이세요.",
   },
 ];
 
 export default function StudyStreamKr() {
   return (
     <div className="min-h-screen bg-background">
       <SEO 
         title="스터디 스트림 | CSW | Online Workspace | Productivity Tool | Study Stream"
         description="무료 스터디 스트림으로 전 세계 학생들과 함께 공부하세요. 실시간 동료 학습, 포모도로 타이머, 로파이 음악으로 집중력을 높이세요."
         keywords="스터디 스트림, 온라인 스터디, 공부 방송, 함께 공부하기, 스터디 그룹, 공부 타이머"
         canonicalUrl="/kr/study-stream/"
       />
       <Header />
       
       <main>
         {/* Hero Section */}
         <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
           <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
           <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
           
           <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
             <div className="grid lg:grid-cols-2 gap-12 items-center">
               <div className="text-left space-y-8 animate-fade-in">
                 <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                   <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                   <span className="text-sm font-medium text-muted-foreground">함께 공부하세요</span>
                 </div>
                 
                 <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                   <span className="text-gradient">스터디 스트림</span>
                 </h1>
                 
                 <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl">
                   전 세계 학생들과 함께 실시간으로 공부하세요. 무료이고 제한이 없습니다.
                 </p>
                 
                 <div className="flex flex-col sm:flex-row gap-4 pt-4">
                   <Button 
                     size="lg" 
                     className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                     asChild
                   >
                     <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                       스트림 시작하기
                       <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                     </a>
                   </Button>
                 </div>
                 
                 <div className="flex items-center gap-8 pt-8">
                   <div>
                     <p className="text-3xl font-bold text-gradient">24/7</p>
                     <p className="text-sm text-muted-foreground">항상 열려있음</p>
                   </div>
                   <div className="w-px h-12 bg-border" />
                   <div>
                     <p className="text-3xl font-bold text-gradient">무제한</p>
                     <p className="text-sm text-muted-foreground">시간 제한 없음</p>
                   </div>
                   <div className="w-px h-12 bg-border" />
                   <div>
                     <p className="text-3xl font-bold text-gradient">무료</p>
                     <p className="text-sm text-muted-foreground">영원히</p>
                   </div>
                 </div>
               </div>
               
               <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                 <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                   <video 
                     src="/videos/productivity-demo.mp4" 
                     autoPlay
                     loop
                     muted
                     playsInline
                     className="w-full h-auto"
                   />
                   <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                 </div>
                 
                 <div className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-md border border-border rounded-2xl p-6 shadow-xl">
                   <div className="flex items-center justify-between">
                     <div>
                       <p className="text-sm text-muted-foreground mb-1">실시간 학습</p>
                       <p className="text-xl font-bold text-foreground">함께 집중하세요</p>
                     </div>
                     <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                       <Play className="w-6 h-6 text-primary-foreground" />
                     </div>
                   </div>
                 </div>
               </div>
             </div>
           </div>
         </section>
 
         {/* Features Grid */}
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
           
           <div className="container mx-auto max-w-6xl relative z-10">
             <div className="text-center mb-16 animate-fade-in">
               <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                 왜 <span className="text-gradient">스터디 스트림</span>인가요?
               </h2>
               <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                 과학적으로 검증된 학습 방법으로 집중력과 생산성을 높이세요
               </p>
             </div>
 
             <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
               {features.map((feature) => {
                 const Icon = feature.icon;
                 return (
                   <Card
                     key={feature.title}
                     className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl"
                   >
                     <CardContent className="p-8 space-y-4">
                       <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                         <Icon className="w-6 h-6 text-primary" />
                       </div>
                       
                       <h3 className="text-xl font-semibold text-foreground font-display">
                         {feature.title}
                       </h3>
                       
                       <p className="text-muted-foreground leading-relaxed">
                         {feature.description}
                       </p>
                     </CardContent>
                   </Card>
                 );
               })}
             </div>
           </div>
         </section>
 
         {/* CTA Section */}
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
           
           <div className="max-w-4xl mx-auto text-center relative z-10">
             <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
               지금 바로 <span className="text-gradient">함께 공부</span>하세요
             </h2>
             <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
               전 세계 수천 명의 학생들이 이미 CSW에서 함께 공부하고 있습니다.
             </p>
             <Button 
               size="lg" 
               className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
               asChild
             >
               <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                 무료로 시작하기
                 <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
               </a>
             </Button>
           </div>
         </section>
 
         {/* Related Pages */}
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
           
           <div className="container mx-auto max-w-6xl relative z-10">
             <div className="text-center mb-12">
               <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                 더 많은 <span className="text-gradient">기능</span> 살펴보기
               </h2>
             </div>
             
             <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
               {[
                 { name: "온라인 작업 공간", path: "/kr/online-workspace/" },
                 { name: "스터디 타이머", path: "/kr/study-timer/" },
                 { name: "포모도로 기법", path: "/kr/pomodoro-technique/" },
                 { name: "함께 공부하기", path: "/kr/study-with-me/" },
               ].map((link) => (
                 <Link
                   key={link.path}
                   to={link.path}
                   className="group p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-lg text-center"
                 >
                   <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                     {link.name}
                   </span>
                 </Link>
               ))}
             </div>
           </div>
         </section>
       </main>
       
       <Footer />
     </div>
   );
 }